import './SeoSetup.scss';

import React, { ChangeEvent, FC, useContext, useEffect, useState } from 'react';
import { AppContext, NotificationType } from 'components/AppContext/AppContext';
import NavHeading, { NavHeadingProps } from 'components/NavHeading/NavHeading';
import { NavHeadings } from 'constants/Constants';
import { useGuidedNavigationCollection } from 'customHooks/useGuidedNavigationCollection';
import { useLocalStorage } from 'customHooks/useLocalStorage';
import { useModifyEntityService } from 'customHooks/useModifyEntityService';
import { useTokenExpiredHandler } from 'customHooks/useTokenExpiredHandler';
import { BatchJob, JobStatus } from 'models/BatchJob';
import { Entities } from 'models/Entities';
import { HttpMethod } from 'models/HttpMethod';
import moment from 'moment';
import { Button, Form, InputGroup, Row, Table } from 'react-bootstrap';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface SeoSetupProps { }

const SeoSetup: FC<SeoSetupProps> = () => {
  const { selectedProjectId, projects, configRepo, notify, sendLog } = useContext(AppContext);
  const [siteMapUrl, setSiteMapUrl] = useState('');
  const [projectBaseUrl, setProjectBaseUrl] = useState('');
  const onTokenExpired = useTokenExpiredHandler();

  const defaultBatchJob: BatchJob = {
    id: 0,
    jobId: undefined,
    eventTime: new Date(),
    createdAt: new Date(),
    jobStatus: JobStatus.NotStarted,
    jobLog: []
  };
  const [batchJob, setBatchJob] = useLocalStorage<BatchJob>(() => { }, "seo-sitemap-batch-id", defaultBatchJob);

  const { executeEntityAction: executeIngestSitemap } =
    useModifyEntityService<BatchJob>(
      configRepo!.CRAWLER_API_BASE_URI,
      Entities.IngestSitemap,
      sendLog,
      onTokenExpired,
    );

  const { executeEntityAction: executeCheckBatchStatus } =
    useModifyEntityService<BatchJob>(
      configRepo!.CRAWLER_API_BASE_URI,
      Entities.BatchJobs,
      sendLog,
      onTokenExpired,
    );

  useEffect(() => {
    const selectedProject = projects.find(p => p.id === selectedProjectId);
    let sitemap = '';
    if (selectedProject) {
      setProjectBaseUrl(selectedProject!.websiteUrl);
      if (selectedProject!.websiteUrl?.endsWith('/')) {
        sitemap = `${selectedProject!.websiteUrl}post-sitemap.xml`;
      } else {
        sitemap = `${selectedProject!.websiteUrl}/post-sitemap.xml`;
      }
    }
    setSiteMapUrl(sitemap);
  }, [selectedProjectId, projects]);

  const tour = useGuidedNavigationCollection();
  useEffect(() => {
    tour.startTourFromQueryParams();
  }, []);

  const onSiteMapUrlChanged = (e: ChangeEvent<HTMLInputElement>) => {
    setSiteMapUrl(e.target.value);
  };

  const onSubmit = async (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!siteMapUrl.startsWith(projectBaseUrl)) {
      notify(
        'Sitemap URL must start with the project\'s Website URL.',
        NotificationType.Error
      );
      return;
    }

    const result: Response = await executeIngestSitemap(
      `?projectId=${selectedProjectId}&sitemapUrl=${encodeURI(siteMapUrl)}`,
      HttpMethod.POST,
      undefined) as Response;
    if (result.ok) {
      const job = await result.json();
      setBatchJob(job);
      notify(
        'Sitemap has been submitted... Check back in approximately 20 minutes to view the completed analysis.',
        NotificationType.Info
      );
    } else if (result.status === 404) {
      notify(
        'Sitemap URL is invalid.  Please update the sitemap URL and try again.',
        NotificationType.Error
      );
    } else if (result.status === 422) {
      notify(
        'Sitemap URL contained invalid XML.  Please check the sitemap URL and validate the XML schema of the sitemap before trying again.',
        NotificationType.Error
      );
    } else {
      notify(
        'An unknown error occurred.  Please update the sitemap URL and try again.  If you continue to receive issues, please contact support.',
        NotificationType.Error
      );
    }


  };

  const onCheckCrawlStatus = async (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const result: Response = await executeCheckBatchStatus(`${batchJob.jobId}`, HttpMethod.GET, undefined) as Response;
    if (result.ok) {
      const job: BatchJob = await result.json();
      setBatchJob(job);
      notify('Updated sitemap submission with latest status', NotificationType.Info);
    }
  };

  let statusName = '';
  let statusClass = 'info';
  if (batchJob) {
    const job: BatchJob = batchJob as BatchJob;
    switch (job.jobStatus) {
      case JobStatus.Error:
        statusClass = 'danger';
        statusName = 'Error';
        break;
      case JobStatus.NotStarted:
        statusClass = 'info';
        statusName = 'Pending';
        break;
      case JobStatus.Success:
        statusClass = 'success';
        statusName = 'Success';
        break;
      case JobStatus.InProgress:
      default:
        statusClass = 'warning';
        statusName = 'In Progress';
        break;
    }
  }

  const heading: NavHeadingProps = {
    title: 'SEO Setup',
    ...NavHeadings.SeoSetup,
  };

  return (
    <div className="seo-setup container">
      <NavHeading {...heading} />

      <Form as={Row} className="my-3">
        <InputGroup>
          <Form.Control
            id="sitemapUrl"
            onChange={onSiteMapUrlChanged}
            value={siteMapUrl} />
          <InputGroup.Text>
            <Button type="submit"
              id="submitSitemap"
              onClick={onSubmit}>
              Process Sitemap
            </Button>
          </InputGroup.Text>
        </InputGroup>
        <Row>
          <Form.Text muted>
            For Wordpress users, please enter the name of the <strong>posts</strong> sitemap XML file.
            Typically this is <code>`/post-sitemap.xml`</code>.
          </Form.Text>
        </Row>
      </Form>

      {(batchJob && batchJob.jobId) ?
        <Row className="my-3" id="crawlStatus">
          <h2>Last Sitemap Submission Details</h2>
          <Table responsive>
            <thead>
              <tr>
                <th>Submission Id</th>
                <th>Submission Status</th>
                <th>Trigger Date</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{batchJob.jobId}</td>
                <td><strong className={`text-${statusClass}`} id="lastSubmitStatus">{statusName}</strong></td>
                <td>{moment(batchJob.eventTime).format('MM/DD/YYYY')}</td>
              </tr>
            </tbody>
          </Table>
          <button type="button"
            onClick={onCheckCrawlStatus}
            id="lastSubmitStatus"
            className="btn btn-info">
            Check crawl status
          </button>
        </Row>
        : ""
      }

    </div>
  );
}

export default SeoSetup;
